import { PageController } from '@client/components/_core/layout/PageController';
import { theme as baseTheme } from '@client/design-system/__generated__';
import { useMediaQuery } from '@client/hooks/ui/useMediaQuery';
import { useThemeStore } from '@client/stores/themeStore';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { ConfigProvider, type ThemeConfig } from 'antd';
import { useLayoutEffect, useMemo } from 'react';

/**
 * Hosted lodgement (CUR-1410): the claimant-facing pages under `/lodge`. A sibling of `/public`
 * with the same forced light theme and no navigation; each page authenticates with an opaque
 * link key, a draft access pair or an email token — never a session.
 */
export const Route = createFileRoute('/lodge')({ component: LodgeLayout });

/**
 * iOS zooms the page in when a focused field's text is under 16px and does not zoom back out, so
 * the card stays wider than the screen for the rest of the form. Fields are 16px on a touch
 * device and at phone widths, where the native date input already is; everything else keeps the
 * 14px body size.
 */
const TOUCH_FIELD_QUERY = '(pointer: coarse), (max-width: 768px)';
const TOUCH_FIELD = { fontSize: 16 };
/**
 * antd pads a single Select from its `fontHeight` token (22px from the 14px seed), which a
 * component `fontSize` does not re-derive, so the Select alone grew to 35px. Re-deriving it as antd
 * does, round(16 × the seed's 22/14 line height), keeps it 32px like the inputs. The token is
 * internal and left out of antd's typings, hence a separate object rather than a checked literal.
 */
const TOUCH_SELECT = { ...TOUCH_FIELD, fontHeight: 25 };
const TOUCH_FIELD_COMPONENTS = {
    Input: TOUCH_FIELD,
    InputNumber: TOUCH_FIELD,
    Select: TOUCH_SELECT,
    DatePicker: TOUCH_FIELD,
} satisfies ThemeConfig['components'];

function LodgeLayout() {
    const setForcedTheme = useThemeStore((state) => state.setForcedTheme);
    const isTouch = useMediaQuery(TOUCH_FIELD_QUERY);
    const theme = useMemo(
        () => ({
            ...baseTheme,
            inherit: false,
            components: isTouch ? { ...baseTheme.components, ...TOUCH_FIELD_COMPONENTS } : baseTheme.components,
        }),
        [isTouch],
    );

    useLayoutEffect(() => {
        setForcedTheme('light');
        return () => setForcedTheme(null);
    }, [setForcedTheme]);

    return (
        <PageController route={Route}>
            <ConfigProvider theme={theme}>
                {/* The document itself never scrolls (`html { overflow: hidden }` in global.css), so
                    this container does, like `/public`; `scrollHostedToTop` targets it by the data attribute. */}
                <div
                    data-hosted-scroll
                    className="h-screen w-full overflow-y-auto overflow-x-hidden overscroll-contain bg-muted"
                >
                    <Outlet />
                </div>
            </ConfigProvider>
        </PageController>
    );
}
